import { parsePhoneNumberFromString } from "libphonenumber-js/min";

import { EmptyPhoneNumber } from "@constants";

import type { IPhoneNumber } from "@interfaces";

const DEFAULT_CODE = "+98";

/**
 * converts phone number string from server to phone dropdown
 * @param value phone number like "+98-9141231234"
 * @returns {IPhoneNumber}
 */
export function toPhone(value?: string): IPhoneNumber {
  if (!value) {
    return EmptyPhoneNumber;
  }

  try {
    const phone = parsePhoneNumberFromString(value);

    if (!phone) {
      return EmptyPhoneNumber;
    }

    const code = `+${phone.countryCallingCode}`;
    const number = phone.nationalNumber;

    return {
      code,
      number: formatNumber({ code, number }),
    };
  } catch {
    return EmptyPhoneNumber;
  }
}

/**
 * Encodes a phone number by concatenating the country code and phone number with a hyphen.
 * @param value - An object containing the country code and phone number of a phone number.
 * @returns A string representing the encoded phone number.
 */
export function encodePhone(value: IPhoneNumber): string {
  if (!value.number) {
    return "";
  }

  return `${value.code || DEFAULT_CODE}-${value.number.replaceAll(/\s/g, "")}`;
}

/**
 * convert phone number value to INTERNATIONAL format (937 989 4747)
 * @param value phone number object
 * @returns
 */
export function formatNumber(value: IPhoneNumber): string {
  const code = value.code || DEFAULT_CODE;

  try {
    const phone = parsePhoneNumberFromString(`${code}${value.number}`);

    if (!phone) {
      return value.number;
    }

    return phone.formatInternational().replace(code, "").trim();
  } catch {
    return value.number;
  }
}

/**
 * convert phone number value to INTERNATIONAL format WITH code (+98 937 989 4747)
 * @param value phone number object
 * @returns
 */
export function formatNumberWithCode(value: IPhoneNumber): string {
  const code = value.code || DEFAULT_CODE;

  try {
    const phone = parsePhoneNumberFromString(`${code}${value.number}`);

    return phone ? phone.formatInternational() : value.number;
  } catch {
    return value.number;
  }
}

/**
 * indicate if the phone is valid or not
 * @param value phone number object
 * @returns return undefined when an error ocurred. for example when the length of number is too short
 */
export function isPhoneValid(value: IPhoneNumber): boolean | undefined {
  const code = value.code || DEFAULT_CODE;

  try {
    const phone = parsePhoneNumberFromString(`${code}${value.number}`);

    return phone?.isValid();
  } catch {
    return undefined;
  }
}
